// Presentation-free: owns the decision, never the messaging — init() rejects with a readable error
// (see getDealerAccessError() below), SecureOutlet renders the error screen. init()
// raises no toast for this gate (post-PR fix, AVS-4455) — the rejected promise is the sole channel.
import { State } from 'utils/state';
import { tenantConfig } from 'utils/tenant';
import { user } from 'utils/auth/user';
import { checkDealerAccess, IDealerAccessResult } from 'utils/auth/dealerAccess';
import { CustomerFacingError } from 'utils/ui/errors';
import { EErrorCode } from 'config/errors-codes';
import logger from 'utils/log';

export interface IDealerAccessState {
  granted: boolean;
}

class DealerAccessState extends State<IDealerAccessState> {
  constructor() {
    super({ granted: false });
  }
}

export const dealerAccessState = new DealerAccessState();

// Module-local, not reactive: no consumer should read/derive UI off these directly.
let inFlight: Promise<IDealerAccessResult> | null = null;
let lastCheckedDealerId: string | null = null;
let lastResult: IDealerAccessResult | null = null;
let cachedError: CustomerFacingError | null = null;
let checksEnabled = false;
let evaluating = false;
// Guards the narrow window between reading user.dealerId (which can synchronously invoke the
// wrapped getUserInfoCallback installed by dealerIdObserver.ts) and assigning `inFlight` below —
// see ensureDealerAccess().
let resolvingDealerId = false;
// Set when maybeRevalidateDealerAccess() observes a dealerId change while a check is already in
// flight for the previous identity. The `evaluating` guard in maybeRevalidateDealerAccess() bails
// out in that case without starting a new check — but the caller that reported the change (the
// getUserInfoCallback observer in dealerIdObserver.ts) only reports each change once, so without
// this flag that changed identity would never be re-checked. ensureDealerAccess()'s outer
// `.finally()` consults this flag once the in-flight check settles and re-triggers.
let pendingRevalidation = false;

async function evaluate(dealerId: string): Promise<IDealerAccessResult> {
  evaluating = true;
  // A fresh decision always starts with a clean error slot. getDealerAccessError()'s cache exists
  // only so that multiple callers resolving the SAME single-flighted decision see one identical
  // error instance (and so CustomerFacingError's constructor, which reports the error once, only
  // fires once for that one decision) — it must never let a later, separate decision reuse an
  // error instance built for an earlier one.
  cachedError = null;
  try {
    // Fast path 1: customers are unaffected by the whitelist.
    if (!user.isDealer) {
      dealerAccessState.setValues({ granted: true });
      lastCheckedDealerId = dealerId;
      const result: IDealerAccessResult = { status: 'allowed' };
      lastResult = result;
      return result;
    }

    await tenantConfig().configLoaded;

    // Fast path 2: whitelisting is disabled for this tenant.
    if (tenantConfig().getAccessControl().dealerWhitelistEnabled === false) {
      dealerAccessState.setValues({ granted: true });
      lastCheckedDealerId = dealerId;
      const result: IDealerAccessResult = { status: 'allowed' };
      lastResult = result;
      return result;
    }

    const brand = tenantConfig().brand;
    const market = tenantConfig().market;

    // Fast path 3: brand/market unresolved yet, mirrors the standalone `if (brand && market)` guard.
    if (!brand || !market) {
      dealerAccessState.setValues({ granted: true });
      lastCheckedDealerId = dealerId;
      const result: IDealerAccessResult = { status: 'allowed' };
      lastResult = result;
      return result;
    }

    // Fast path 4: deliberate parity with standalone SecureOutlet — a dealer without a resolved
    // dealerId yet is granted, but the decision must NEVER be cached: the moment a real dealerId
    // appears it has to be checked fresh, so `lastCheckedDealerId` is left as-is.
    if (!dealerId) {
      dealerAccessState.setValues({ granted: true });
      const result: IDealerAccessResult = { status: 'allowed' };
      lastResult = result;
      return result;
    }

    const result = await checkDealerAccess({ brand, market, dealerId });

    // Invariant: state (dealerAccessState/lastResult/lastCheckedDealerId) is only ever written for
    // the identity that is STILL CURRENT when this check settles. Re-reading user.dealerId here can
    // synchronously invoke the wrapped getUserInfoCallback installed by dealerIdObserver.ts, which
    // calls maybeRevalidateDealerAccess() reentrantly — but `evaluating` is still true at that
    // point (it only resets in this function's own `finally`), so that reentrant call only sets
    // `pendingRevalidation` below, the same flag, idempotently. It never starts a second evaluate().
    if (user.dealerId !== dealerId) {
      // The identity moved on while this check was in flight — a verdict for a dealer the user has
      // since switched away from must never overwrite dealerAccessState/lastResult/lastCheckedDealerId.
      // Flag a revalidation instead: ensureDealerAccess()'s outer .finally() re-triggers a check for
      // the now-current identity once this settles. This also covers identity changes no observer
      // reported, such as a direct sessionStorage write.
      pendingRevalidation = true;
      return result;
    }

    dealerAccessState.setValues({ granted: result.status === 'allowed' });
    // A 'failed' result (transport/network failure, documented as retryable) must NOT be cached
    // the way a final 'allowed'/'denied' decision is: caching it would make the cache-hit check in
    // ensureDealerAccess() hand a host that retries init() the same stale failure forever, without
    // ever issuing a new request. Leaving lastCheckedDealerId/lastResult untouched here means the
    // next ensureDealerAccess() call for this dealerId is a genuine cache miss.
    if (result.status !== 'failed') {
      lastCheckedDealerId = dealerId;
      lastResult = result;
    }
    return result;
  } finally {
    evaluating = false;
  }
}

/**
 * Only call this from an authoritative entry point (init(), SecureOutlet, or a guarded re-entry
 * point such as `maybeRevalidateDealerAccess`) — never speculatively before an authoritative entry
 * point has resolved the user/session, since it will happily grant on an unresolved dealerId.
 */
export async function ensureDealerAccess(): Promise<IDealerAccessResult> {
  checksEnabled = true;

  if (inFlight) {
    return inFlight;
  }

  let dealerId: string;
  resolvingDealerId = true;
  try {
    // Reading user.dealerId can synchronously invoke the wrapped getUserInfoCallback installed by
    // dealerIdObserver.ts. On the first call in a fresh session, that observer's module-level
    // lastSeenIdentity is still unset, so this very read looks like "a change" and would otherwise
    // trigger a reentrant maybeRevalidateDealerAccess() synchronously — before `inFlight` below is
    // assigned — starting a second, redundant evaluate() call for the dealerId we're already about
    // to check. `resolvingDealerId` suppresses that: `evaluating` alone doesn't cover this window
    // since it's only set once evaluate() itself starts, which is after this read.
    dealerId = user.dealerId;
  } finally {
    resolvingDealerId = false;
  }

  if (dealerId && lastCheckedDealerId === dealerId && lastResult) {
    return lastResult;
  }

  inFlight = evaluate(dealerId).finally(() => {
    inFlight = null;
    // Must run here — after inFlight is cleared — not inside evaluate()'s own internal
    // try/finally. evaluate()'s promise (and its internal finally, which only resets
    // `evaluating`) settles before this chained .finally() runs, so by the time this callback
    // executes both `evaluating` is already false and `inFlight` becomes null within this same
    // synchronous callback — the correct state for maybeRevalidateDealerAccess() to start a
    // genuinely fresh check rather than returning the (now-settled) stale inFlight promise.
    if (pendingRevalidation) {
      pendingRevalidation = false;
      maybeRevalidateDealerAccess();
    }
  });

  return inFlight;
}

export function resetDealerAccess(): void {
  // checksEnabled is a one-way latch — must survive an in-app dealer switch so
  // maybeRevalidateDealerAccess() keeps working without a full page reload.
  dealerAccessState.setValues({ granted: false });
  lastResult = null;
  cachedError = null;
  lastCheckedDealerId = null;
}

// Test-only: production code must never re-disable the checksEnabled latch.
export function __resetDealerAccessLatchForTests(): void {
  checksEnabled = false;
}

export function isDealerAccessGranted(): boolean {
  return dealerAccessState.get('granted');
}

// Handles a rejected re-check on the fire-and-forget path (maybeRevalidateDealerAccess() below,
// and its .finally()-triggered re-entry in ensureDealerAccess()). Mirrors how a resolved 'failed'
// result is already handled on this same path: sets granted false and caches nothing, so the next
// call is a genuine cache miss. Deliberately does NOT call getDealerAccessError() — that builds a
// CustomerFacingError, whose constructor calls window.E3D.reportError() and emits a host-visible
// "error" event, which a 'failed' status never does on this silent fire-and-forget path. No
// try/catch here: this function IS the catch handler, so there is nothing to wrap.
function handleRevalidationFailure(error: unknown): void {
  dealerAccessState.setValues({ granted: false });
  logger.error(`[dealerAccess] revalidation failed (${EErrorCode.DEALER_ACCESS_CHECK_FAILED})`, error);
}

/**
 * Fire-and-forget revalidation for when the dealerId changes after an initial grant (e.g. a grant
 * made without a resolved dealerId must still be revisited once a real one appears).
 *
 * A rejected re-check (e.g. a thrown error while resolving `tenantConfig().configLoaded`) is
 * caught by `handleRevalidationFailure` above: it fails closed (`granted: false`), logs once via
 * `logger.error`, and caches nothing — never an unhandled promise rejection.
 */
export function maybeRevalidateDealerAccess(): void {
  if (!checksEnabled) {
    return;
  }
  if (evaluating) {
    // A check is already in flight, possibly for an identity this change has already superseded.
    // The caller that got us here (dealerIdObserver.ts) only reports each identity change once, so
    // dropping this silently would leave the new identity unchecked indefinitely. Flag it instead —
    // ensureDealerAccess()'s outer .finally() re-triggers once the in-flight check settles.
    pendingRevalidation = true;
    return;
  }
  if (resolvingDealerId) {
    return;
  }
  if (user.dealerId === lastCheckedDealerId) {
    return;
  }
  void ensureDealerAccess().catch(handleRevalidationFailure);
}

// CustomerFacingError resolves its default message/title through Lingui `msg` descriptors'
// `.id`, which is the compiled catalog id (an opaque hash) at real runtime — readable English
// only coincidentally under vitest, where `.id` happens to equal the source string (see
// src/utils/ui/errors.ts). getDealerAccessError() is only ever called with these two codes
// (confirmed via `grep -rn getDealerAccessError src/`), so hardcoding their plain-English copy
// here — sourced from src/config/ui/error-collection.ts's entries for the same codes — is a
// deliberate, narrowly-scoped override via CustomerFacingError's `options` parameter, not a
// bypass of the shared error system. Keep this in sync with error-collection.ts if either changes.
const DEALER_ACCESS_ERROR_COPY: Partial<Record<EErrorCode, { title: string; message: string }>> = {
  [EErrorCode.DEALER_NOT_WHITELISTED]: {
    title: 'Access Restricted',
    message:
      'Your dealer account is not authorized to access this application. Please contact your administrator for access.',
  },
  [EErrorCode.DEALER_ACCESS_CHECK_FAILED]: {
    title: 'Access check failed',
    message: 'We could not verify your access right now. Please try again in a moment.',
  },
};

export function getDealerAccessError(code: EErrorCode): CustomerFacingError {
  if (!cachedError) {
    cachedError = new CustomerFacingError(code, DEALER_ACCESS_ERROR_COPY[code]);
  }
  return cachedError;
}
